// ═══════════════════════════════════════════════════════════════
// Handoff — All Handoff Notes
// Browse all handoffs across events with filtering
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Select, TextInput, Button,
} from '../../components/gab/GabComponents';
import { IconSearch, IconSun, IconMoon, IconPlus } from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { primary } from '../../lib/colors';

export default function AllHandoffs() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('handoff');
  const { notes, events: handoffEvents, fetchAll, fetchEvents } = useHandoffStore();
  const { incidents, activeIncident, loadIncidents } = useAuthStore();
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => { loadIncidents(); fetchAll(); }, []);
  usePolling(() => { fetchAll(); });

  // Store already filtered to active incident via fetchAll(incidentId)
  const eventOptions = [
    { value: 'all', label: 'All Events' },
    ...handoffEvents.map(e => ({ value: String(e.id), label: e.eventName || `Event #${e.id}` })),
  ];

  const filtered = notes
    .filter(n => statusFilter === 'all' || n.status === statusFilter)
    .filter(n => eventFilter === 'all' || String(n.eventId) === eventFilter)
    .filter(n => !search || (n.positionName || '').toLowerCase().includes(search.toLowerCase()) || (n.senderName || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));

  useEffect(() => { setPage(1); }, [search, statusFilter, eventFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const noteStatusColor = (s: string) => s === 'draft' ? '#939598' : s === 'submitted' ? '#C68700' : 'green';

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">All Handoffs</Title>
        {canEdit && (
        <Button
          leftSection={<IconPlus size={14} />}
          size="xs"
          onClick={() => navigate('/handoff/new')}
        >
          New Handoff
        </Button>
        )}
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} size="xs" w={200} />
        <Select data={[{ value: 'all', label: 'All Status' }, { value: 'draft', label: 'Draft' }, { value: 'submitted', label: 'Submitted' }, { value: 'acknowledged', label: 'Acknowledged' }]}
          value={statusFilter} onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={160} />
        <Select data={eventOptions} value={eventFilter} onChange={v => setEventFilter(v ?? 'all')} size="xs" w={220} />
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No handoffs found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {pageRows.map(n => {
            const hEvt = handoffEvents.find(e => e.id === Number(n.eventId));
            const inc = hEvt?.incidentId != null ? incidents.find(i => i.id === Number(hEvt.incidentId)) : undefined;
            const eventLabel = inc?.name || hEvt?.eventName || `Event #${n.eventId}`;
            const ShiftIcon = n.fromShift === 'day' ? IconSun : IconMoon;
            return (
              <Paper key={n.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer', borderLeft: `3px solid ${noteStatusColor(n.status) === '#C68700' ? 'var(--color-warning)' : noteStatusColor(n.status) === 'var(--color-success)' ? 'var(--color-success)' : '#ccc'}` }}
                onClick={() => navigate(`/handoff/note/${n.id}`)}>
                <Group justify="space-between">
                  <div>
                    <Group gap="xs" mb={2}>
                      <ShiftIcon size={14} color={n.fromShift === 'day' ? 'var(--color-warning)' : primary[700]} />
                      <Text size="sm" fw={500}>{n.positionName}</Text>
                      <Badge size="xs" color={noteStatusColor(n.status)} tt="capitalize">{n.status}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {eventLabel} — {n.fromShift} → {n.toShift}
                      {n.senderName && ` — From: ${n.senderName}`}
                    </Text>
                  </div>
                  <Text size="xs" c="dimmed">
                    {n.datecreated ? new Date(n.datecreated).toLocaleDateString() : ''}
                  </Text>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}
    </div>
  );
}
